/**
 * Listenables automatic binding logic for Reflux replacement.
 * Handles automatic wiring of store methods to actions based on naming conventions.
 *
 * @module reflux/listenables
 */

import { createListenerMethods } from './listener.js';
import { capitalize } from './utils.js';
import {
  STANDARD_ACTION_RESULT_NAMES,
  ACTION_RESULT_ALIAS_MAP,
  ACTION_INTERNAL_PROPERTIES,
} from './constants.js';

/**
 * Extracts the base action name from an action object.
 * Uses the definition.actionName if available, otherwise falls back to eventLabel.
 *
 * @param {Object} action - Action object
 * @returns {string} Action name (without parent prefix)
 *
 * @example
 * // For action with definition: { actionName: 'login' }
 * getActionName(action) // 'login'
 *
 * @example
 * // For child action: { definition: { actionName: 'login.complete' } }
 * getActionName(action) // 'complete' (extracts last segment)
 */
const getActionName = (action) => {
  const name = action.definition?.actionName || action.eventLabel || 'action';

  // For child actions like 'login.complete', extract just 'complete'
  const parts = name.split('.');
  return parts[parts.length - 1];
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Checks if a value is a listenable action (has a listen method).
 *
 * @param {*} value - Value to check
 * @returns {boolean} True if value is a listenable action
 */
const isListenableAction = (value) =>
  value && typeof value.listen === 'function';

/**
 * Checks if a child action name is an alias that would cause double-binding.
 * Aliases like 'completed' point to the same action object as 'complete'.
 *
 * @param {string} childName - Child action name to check
 * @param {Object} action - Parent action object
 * @returns {boolean} True if this is an alias that should be skipped
 */
const isAliasChild = (childName, action) => {
  const canonicalName = ACTION_RESULT_ALIAS_MAP.get(childName);
  return canonicalName !== undefined && action[canonicalName] !== undefined;
};

/**
 * Generates the store handler method name for an action.
 *
 * @param {string} actionName - Base action name
 * @param {string} [childName] - Optional child action name
 * @returns {string} Handler method name (e.g., 'onLogin', 'onLoginComplete')
 *
 * @example
 * buildHandlerName('login')            // 'onLogin'
 * buildHandlerName('login', 'complete') // 'onLoginComplete'
 */
const buildHandlerName = (actionName, childName = null) => {
  const base = `on${capitalize(actionName)}`;
  return childName ? `${base}${capitalize(childName)}` : base;
};

/**
 * Attempts to bind a store handler method to an action.
 * Includes fallback logic for actions with non-standard naming (e.g., actions
 * that already have an "on" prefix like "onToggleTab").
 *
 * @param {Object} store - Store with handler methods
 * @param {Object} action - Action to listen to
 * @param {string} handlerName - Expected handler method name (e.g., 'onLogin')
 * @param {string} [fallbackName] - Fallback handler name to try if primary fails
 * @returns {boolean} True if handler was found and bound
 */
const tryBindHandler = (store, action, handlerName, fallbackName = null) => {
  // Try the standard handler name first (e.g., 'onLogin')
  if (typeof store[handlerName] === 'function') {
    store.listenTo(action, store[handlerName]);
    return true;
  }
  // Fallback: try the original action name if it already looks like a handler
  // This matches legacy Reflux v0.4.1 behavior in listenToMany()
  if (fallbackName && typeof store[fallbackName] === 'function') {
    // Warn about non-standard naming convention
    const storeName = store.storeName || 'UnnamedStore';
    // Check if action has "on" prefix (e.g., "onToggleTab" where T is uppercase)
    const hasOnPrefix = fallbackName.startsWith('on') &&
                        fallbackName.length > 2 &&
                        fallbackName[2] === fallbackName[2].toUpperCase();

    if (hasOnPrefix) {
      // Case 1: Action has redundant "on" prefix
      const suggestedAction = fallbackName.charAt(2).toLowerCase() + fallbackName.slice(3);
      console.warn(
        `[Reflux] ${storeName}: Action "${fallbackName}" has redundant "on" prefix. ` +
        `Consider renaming action to "${suggestedAction}".`
      );
    } else {
      // Case 2: Handler missing standard "on" prefix
      console.warn(
        `[Reflux] ${storeName}: Handler "${fallbackName}" missing "on" prefix. ` +
        `Consider renaming to "${handlerName}".`
      );
    }

    store.listenTo(action, store[fallbackName]);
    return true;
  }
  return false;
};

/**
 * Binds standard child actions (complete/fail) to store handlers.
 * Handles alias detection to prevent double-binding.
 *
 * @param {Object} store - Store object
 * @param {Object} action - Parent action with potential child actions
 * @param {string} actionName - Base action name (already extracted)
 */
const bindStandardChildActions = (store, action, actionName) => {
  for (const childName of STANDARD_ACTION_RESULT_NAMES) {
    const childAction = action[childName];

    if (!isListenableAction(childAction)) continue;
    if (isAliasChild(childName, action)) continue;

    const handlerName = buildHandlerName(actionName, childName);
    tryBindHandler(store, childAction, handlerName);
  }
};

/**
 * Binds custom child actions beyond the standard complete/fail pattern.
 * Examples: login.progress, login.timeout, etc.
 *
 * @param {Object} store - Store object
 * @param {Object} action - Parent action with potential custom child actions
 * @param {string} actionName - Base action name (already extracted)
 */
const bindCustomChildActions = (store, action, actionName) => {
  for (const [key, value] of Object.entries(action)) {
    if (ACTION_INTERNAL_PROPERTIES.has(key)) continue;
    if (!isListenableAction(value)) continue;

    const handlerName = buildHandlerName(actionName, key);
    tryBindHandler(store, value, handlerName);
  }
};

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Binds a store to an action group with automatic on<ActionName> method binding.
 * Matches Reflux behavior:
 * - onActionName() for main action
 * - onActionNameComplete() for action.complete
 * - onActionNameFail() for action.fail
 *
 * @param {Object} store - Store object with handler methods
 * @param {Object} actions - Actions object (from createActions) or single action
 *
 * @example
 * const UserActions = createActions({
 *   login: { asyncResult: true, children: ['complete', 'fail'] }
 * });
 *
 * const UserStore = createStore({
 *   listenables: [UserActions],
 *
 *   onLogin(credentials) {
 *     console.log('Login triggered');
 *   },
 *
 *   onLoginComplete(userData) {
 *     console.log('Login succeeded:', userData);
 *   },
 *
 *   onLoginFail(error) {
 *     console.error('Login failed:', error);
 *   }
 * });
 *
 * // Now when UserActions.login() fires, onLogin() is called
 * // When UserActions.login.complete() fires, onLoginComplete() is called
 * // When UserActions.login.fail() fires, onLoginFail() is called
 */
export const bindListenables = (store, actions) => {
  if (!actions) {
    return;
  }

  // Ensure store has listener methods (listenTo, stopListeningTo, etc.)
  if (!store.listenTo) {
    Object.assign(store, createListenerMethods(store));
  }

  // Handle array of action groups: [UserActions, SiteActions, ...]
  if (Array.isArray(actions)) {
    for (const actionGroup of actions) {
      bindListenables(store, actionGroup);
    }
    return;
  }

  // Handle single action object or action group object
  // Two cases:
  // 1. Single action: { trigger, listen, complete, fail, ... }
  // 2. Actions group: { login: {...}, logout: {...}, ... }

  // Detect if this is a single action or an actions group
  const isSingleAction = typeof actions === 'function' && typeof actions.trigger === 'function';

  if (isSingleAction) {
    // Single action: bind directly
    bindSingleAction(store, actions);
  } else if (typeof actions === 'object') {
    // Actions group: iterate over all actions
    for (const [actionName, action] of Object.entries(actions)) {
      if (action && typeof action.listen === 'function') {
        bindSingleAction(store, action, actionName);
      }
    }
  }
};

/**
 * Binds a single action to a store with automatic handler method detection.
 *
 * @param {Object} store - Store object
 * @param {Object} action - Single action object
 * @param {string} [explicitName] - Explicit action name (from actions group key)
 *
 * @example
 * bindSingleAction(store, UserActions.login, 'login');
 * // Binds onLogin, onLoginComplete, onLoginFail methods
 */
const bindSingleAction = (store, action, explicitName) => {
  const actionName = explicitName || getActionName(action);

  // Bind main action to handler (e.g., onLogin)
  // Pass actionName as fallback for actions that already have 'on' prefix
  // (e.g., action 'onToggleTab' → try 'onOnToggleTab', fallback to 'onToggleTab')
  const mainHandlerName = buildHandlerName(actionName);
  tryBindHandler(store, action, mainHandlerName, actionName);

  // Bind standard child actions (complete/fail)
  bindStandardChildActions(store, action, actionName);

  // Bind custom child actions (progress, timeout, etc.)
  bindCustomChildActions(store, action, actionName);
};
